import { useMemo } from 'react'
import { collectionGroup, query, where, orderBy } from 'firebase/firestore'
import { db } from '../libs/Firebase'
import { Subcollections } from '../../../pure-js/libs/Collections'
import { PrivateLessonOrder } from '../../../pure-js/types/GrooverTypes'
import { useFirestoreQuery } from './useFirestoreQuery'
import { ReducerState } from './useFirestoreQueryHelper'
import { usePortalHostQueryGate } from './portalHostQueryGate'

/**
 * Hook to fetch private lesson orders for a specific enrollment user using reactive queries
 *
 * Uses collection group query to search across all clients' private_lesson_orders subcollections.
 * Firestore rules will filter results to only show orders for this enrollmentUserId.
 *
 * The returned PrivateLessonOrder objects may contain either:
 * - New structure: defaultInstructor/defaultLocation reference objects with id and name
 * - Legacy structure: instructorId/instructorName scalar fields
 * - Both structures (during migration)
 *
 * Use helper functions from PrivateLessonHelpers to access default instructor/location data:
 * - getDefaultInstructorId(order) - Gets default instructor ID with fallback to legacy field
 * - getDefaultInstructorName(order) - Gets default instructor name with fallback to legacy field
 * - getInstructorId(order) - Generic function that works for both PrivateLesson and PrivateLessonOrder
 * - getInstructorName(order) - Generic function that works for both PrivateLesson and PrivateLessonOrder
 * - getLocationId(order) - Gets location ID with fallback to legacy field
 * - getLocationName(order) - Gets location name from reference object
 *
 * @param enrollmentUserId - The enrollment user ID from usePortalAuth, or null if not authenticated
 * @param clientId - Optional client to filter to (from useClientRouteHost()). When provided, adds
 *   a `where('clientId', '==', clientId)` filter — used by client-specific Portal subdomains (AC-1,
 *   COU-334). Requires a composite index (enrollmentUserId, clientId, createdAt) on the
 *   private_lesson_orders collection group; see firestore.indexes.json. Omitted/undefined for the
 *   unified portal, which shows all clients' data.
 * @returns ReducerState with orders data, loading state, and error
 */
export const usePrivateLessonOrdersForUser = (
  enrollmentUserId: string | null,
  clientId?: string
): ReducerState<PrivateLessonOrder[]> => {
  const { readyUserId, hostLoading } = usePortalHostQueryGate(enrollmentUserId)

  const firestoreQuery = useMemo(() => {
    if (!readyUserId) {
      // Return dummy collection to avoid permission errors when no user is authenticated
      return collectionGroup(db, 'dummy-orders')
    }

    // Use collection group query to search across all private_lesson_orders subcollections
    // This matches the Firestore rules which allow reading from collection group
    const constraints = [where('enrollmentUserId', '==', readyUserId)]
    if (clientId) {
      constraints.push(where('clientId', '==', clientId))
    }
    return query(
      collectionGroup(db, Subcollections.PRIVATE_LESSON_ORDERS),
      ...constraints,
      orderBy('createdAt', 'desc')
    )
  }, [readyUserId, clientId])

  const result = useFirestoreQuery<PrivateLessonOrder[]>(firestoreQuery, {
    fnName: 'usePrivateLessonOrdersForUser',
    enabled: !!readyUserId
  })

  return {
    ...result,
    isLoading: hostLoading || result.isLoading,
    status: hostLoading ? 'loading' : result.status
  }
}
